class CandyMixSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.maxSelections = Number(this.dataset.maxSelections || 5);
    this.optionName = this.dataset.optionName || 'Flavor';
    this.optionIndex = Number(this.dataset.optionIndex || 0);
    this.singularLabel = this.dataset.singularLabel || 'Flavor';
    this.pluralLabel = this.dataset.pluralLabel || 'Flavors';
    this.productId = this.dataset.productId || '';
    this.form = document.getElementById(this.dataset.formId || '');
    this.choices = Array.from(this.querySelectorAll('[data-mix-choice]'));
    this.propertyInput = this.querySelector('[data-mix-property]');
    this.counter = this.querySelector('[data-mix-counter]');
    this.error = this.querySelector('[data-mix-error]');
    this.variants = this.parseJson('[data-mix-variants]', []);
    this.optionNames = this.parseJson('[data-mix-option-names]', []);
    this.isSettingAnchor = false;

    this.handleChoiceChange = this.handleChoiceChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleNativeChange = this.handleNativeChange.bind(this);
    this.refreshNativePicker = this.refreshNativePicker.bind(this);

    this.choices.forEach((choice) => choice.addEventListener('change', this.handleChoiceChange));
    this.form?.addEventListener('submit', this.handleFormSubmit, true);

    this.refreshNativePicker();
    this.updateSelectionState();
  }

  disconnectedCallback() {
    this.choices?.forEach((choice) => choice.removeEventListener('change', this.handleChoiceChange));
    this.form?.removeEventListener('submit', this.handleFormSubmit, true);
    this.detachPicker();
  }

  parseJson(selector, fallback) {
    try {
      const text = this.querySelector(selector)?.textContent || '';
      return JSON.parse(text);
    } catch (error) {
      console.warn('[Candy Mix] Could not parse product data.', error);
      return fallback;
    }
  }

  getProductRoot() {
    return this.closest('product-information, featured-product-information, .shopify-section') || document;
  }

  findPicker() {
    const root = this.getProductRoot();
    const matchingPicker = Array.from(root.querySelectorAll('variant-picker')).find(
      (picker) => picker.dataset.productId === this.productId
    );
    return matchingPicker || root.querySelector('variant-picker');
  }

  refreshNativePicker() {
    const nextPicker = this.findPicker();
    if (!nextPicker) return;

    if (this.picker !== nextPicker) {
      this.detachPicker();
      this.picker = nextPicker;
      this.picker.addEventListener('change', this.handleNativeChange);
      this.pickerObserver = new MutationObserver(() => {
        this.hideNativeOption();
        this.hideAcceleratedCheckout();
      });
      this.pickerObserver.observe(this.picker, { childList: true, subtree: true });
    }

    this.hideNativeOption();
    this.hideAcceleratedCheckout();
    this.refreshChoiceAvailability();
  }

  detachPicker() {
    this.picker?.removeEventListener('change', this.handleNativeChange);
    this.pickerObserver?.disconnect();
    this.picker = null;
    this.pickerObserver = null;
  }

  hideNativeOption() {
    if (!this.picker) return;

    const matchingRadio = Array.from(this.picker.querySelectorAll('input[data-option-name]')).find(
      (input) => this.sameText(input.dataset.optionName, this.optionName)
    );
    const matchingOption = Array.from(this.picker.querySelectorAll('option[data-option-name]')).find(
      (option) => this.sameText(option.dataset.optionName, this.optionName)
    );
    const nativeOption = matchingRadio?.closest('.variant-option') || matchingOption?.closest('.variant-option');
    nativeOption?.classList.add('candy-mix-native-option');
  }

  hideAcceleratedCheckout() {
    const root = this.getProductRoot();
    root.querySelectorAll('.accelerated-checkout-block').forEach((button) => {
      button.hidden = true;
      button.style.display = 'none';
    });
  }

  sameText(left, right) {
    return String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();
  }

  getSelectedChoices() {
    return this.choices.filter((choice) => choice.checked).map((choice) => choice.value);
  }

  getCurrentOptionValues() {
    const values = new Map();
    if (!this.picker) return values;

    this.picker.querySelectorAll('select').forEach((select) => {
      const selected = select.selectedOptions?.[0];
      if (selected?.dataset.optionName) values.set(selected.dataset.optionName, selected.value);
    });

    this.picker.querySelectorAll('fieldset input:checked').forEach((input) => {
      if (input.dataset.optionName) values.set(input.dataset.optionName, input.value);
    });

    return values;
  }

  variantMatchesCurrentOptions(variant, flavorValue) {
    if (!Array.isArray(variant.options)) return false;
    if (variant.options[this.optionIndex] !== flavorValue) return false;

    const currentValues = this.getCurrentOptionValues();
    return this.optionNames.every((optionName, index) => {
      if (index === this.optionIndex) return true;
      const currentValue = currentValues.get(optionName);
      return !currentValue || variant.options[index] === currentValue;
    });
  }

  findAvailableVariant(flavorValue) {
    return this.variants.find(
      (variant) => variant.available && this.variantMatchesCurrentOptions(variant, flavorValue)
    );
  }

  refreshChoiceAvailability() {
    if (!this.picker || !this.variants.length) return;

    let removedUnavailableChoice = false;
    this.choices.forEach((choice) => {
      const available = Boolean(this.findAvailableVariant(choice.value));
      if (!available && choice.checked) {
        choice.checked = false;
        removedUnavailableChoice = true;
      }
      choice.dataset.unavailable = available ? 'false' : 'true';
    });

    if (removedUnavailableChoice) {
      this.showError(`One selected ${this.singularLabel.toLowerCase()} is unavailable in this bag size.`);
      this.updateSelectionState();
    } else {
      this.applyDisabledStates();
    }
  }

  handleNativeChange(event) {
    const target = event.target;
    const selectedOption = target instanceof HTMLSelectElement ? target.selectedOptions?.[0] : target;
    const changedOptionName = selectedOption?.dataset?.optionName;

    if (this.isSettingAnchor || this.sameText(changedOptionName, this.optionName)) return;

    window.setTimeout(() => {
      this.refreshNativePicker();
      this.syncAnchorVariant();
    }, 0);
  }

  handleChoiceChange(event) {
    const choice = event.currentTarget;
    const selected = this.getSelectedChoices();

    if (choice.checked && selected.length > this.maxSelections) {
      choice.checked = false;
      this.showError(`Choose up to ${this.maxSelections} ${this.pluralLabel.toLowerCase()}.`);
    } else {
      this.clearError();
    }

    this.updateSelectionState();
    this.syncAnchorVariant();
  }

  updateSelectionState() {
    const selected = this.getSelectedChoices();
    if (this.propertyInput) this.propertyInput.value = selected.join(', ');
    if (this.counter) this.counter.textContent = `${selected.length} of ${this.maxSelections} selected`;
    this.applyDisabledStates();
  }

  applyDisabledStates() {
    const selectedCount = this.getSelectedChoices().length;
    this.choices.forEach((choice) => {
      const unavailable = choice.dataset.unavailable === 'true';
      const maxReached = selectedCount >= this.maxSelections && !choice.checked;
      choice.disabled = unavailable || maxReached;
    });
  }

  syncAnchorVariant() {
    if (!this.picker) this.refreshNativePicker();
    if (!this.picker) return;

    const selected = this.getSelectedChoices();
    if (!selected.length) return;

    const candidates = selected
      .map((value) => ({ value, variant: this.findAvailableVariant(value) }))
      .filter((candidate) => candidate.variant)
      .sort((left, right) => Number(right.variant.price || 0) - Number(left.variant.price || 0));

    const anchorValue = candidates[0]?.value || selected[0];
    this.selectNativeFlavor(anchorValue);
  }

  selectNativeFlavor(value) {
    if (!this.picker) return;

    const radio = Array.from(this.picker.querySelectorAll('input[data-option-name]')).find(
      (input) => this.sameText(input.dataset.optionName, this.optionName) && input.value === value
    );

    if (radio) {
      if (radio.checked) return;
      this.isSettingAnchor = true;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      window.setTimeout(() => {
        this.isSettingAnchor = false;
        this.refreshNativePicker();
      }, 0);
      return;
    }

    const option = Array.from(this.picker.querySelectorAll('option[data-option-name]')).find(
      (item) => this.sameText(item.dataset.optionName, this.optionName) && item.value === value
    );
    const select = option?.parentElement;

    if (select instanceof HTMLSelectElement && select.value !== value) {
      this.isSettingAnchor = true;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      window.setTimeout(() => {
        this.isSettingAnchor = false;
        this.refreshNativePicker();
      }, 0);
    }
  }

  handleFormSubmit(event) {
    const selected = this.getSelectedChoices();
    if (selected.length > 0 && selected.length <= this.maxSelections) {
      this.clearError();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!selected.length) {
      this.showError(`Please select at least 1 ${this.singularLabel.toLowerCase()}.`);
    } else {
      this.showError(`Choose up to ${this.maxSelections} ${this.pluralLabel.toLowerCase()}.`);
    }

    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.choices.find((choice) => !choice.disabled)?.focus();
  }

  showError(message) {
    if (this.error) this.error.textContent = message;
  }

  clearError() {
    if (this.error) this.error.textContent = '';
  }
}

if (!customElements.get('candy-mix-selector')) {
  customElements.define('candy-mix-selector', CandyMixSelector);
}
